import { getIn } from '../accessor'
import { getIterableKeys, isPrivateKey } from '../helper'
import { getPrototypeOf, isArray, objectPrototype } from '../shortcut'

/**
 * 删除 Diff 项
 */
export type DeleteDifferent = {
  oldValue?: any
  path: PropertyKey[]
  type: 'delete'
}

/**
 * Diff 项
 */
export type Different = DeleteDifferent | SetDifferent

/**
 * 赋值 Diff 项
 */
export type SetDifferent = {
  newValue?: any
  oldValue?: any
  path: PropertyKey[]
  type: 'set'
}

export function applyDiff(
  target: any,
  diffs: Different[],
  getter?: (diff: SetDifferent) => any
): void {
  // 先倒序处理删除，防止删除的时候出现下标偏移的情况
  for (let i = diffs.length - 1; i >= 0; i--) {
    const diff = diffs[i]
    if (diff.type === 'delete') {
      const lastKey = diff.path.at(-1) as number
      const lastTarget = getIn(target, diff.path.slice(0, -1))
      if (isArray(lastTarget)) lastTarget.splice(lastKey, 1)
      else if (typeof lastTarget === 'object' && lastTarget !== null) {
        delete lastTarget[lastKey]
      }
    }
  }
  // 再顺序处理新增和修改
  for (const diff of diffs) {
    if (diff.type === 'set') {
      const lastKey = diff.path.at(-1) as number
      const lastTarget = getIn(target, diff.path.slice(0, -1))
      if (typeof lastTarget === 'object' && lastTarget !== null) {
        lastTarget[lastKey] =
          typeof getter === 'function' ? getter(diff) : diff.newValue
      }
    }
  }
}

export function arrayDiff(
  target?: any,
  source?: any,
  path: PropertyKey[] = [],
  visitor?: (target: any, source: any, path: PropertyKey[]) => Different[]
): Different[] {
  if (target === source) return []
  if (
    (target.length === 0 && source.length > 0) ||
    (source.length === 0 && target.length > 0)
  ) {
    return [
      {
        newValue: source,
        oldValue: target,
        path,
        type: 'set'
      }
    ]
  }
  const diffs: Different[] = []
  for (let i = 0; i < Math.max(target.length, source.length); i++) {
    const newPath = [...path, i]
    if (i >= source.length) {
      diffs.push({
        oldValue: target[i],
        path: newPath,
        type: 'delete'
      })
    } else if (
      isPrimitiveDiff(target[i], source[i]) &&
      target[i] !== source[i]
    ) {
      diffs.push({
        newValue: source[i],
        oldValue: target[i],
        path: newPath,
        type: 'set'
      })
    } else {
      diffs.push(...different(target[i], source[i], newPath, visitor))
    }
  }
  return diffs
}

export function different(
  target?: any,
  source?: any,
  path: PropertyKey[] = [],
  visitor?: (target: any, source: any, path: PropertyKey[]) => Different[]
): Different[] {
  if (target === source) return []
  if (isPrimitiveDiff(target, source))
    return primitiveDiff(target, source, path)
  if (isArray(target)) {
    return arrayDiff(target, source, path, visitor)
  } else if (
    visitor &&
    (getPrototypeOf(target) !== objectPrototype ||
      getPrototypeOf(source) !== objectPrototype)
  ) {
    return visitor(target, source, path) ?? []
  }
  return objectDiff(target, source, path, visitor)
}

export function objectDiff(
  target?: any,
  source?: any,
  path: PropertyKey[] = [],
  visitor?: (target: any, source: any, path: PropertyKey[]) => Different[]
): Different[] {
  if (target === source) return []
  const targetKeys = getIterableKeys(target)
  const sourceKeys = getIterableKeys(source)
  if (
    (targetKeys.length === 0 && sourceKeys.length > 0) ||
    (sourceKeys.length === 0 && targetKeys.length > 0)
  ) {
    return [
      {
        newValue: source,
        oldValue: target,
        path,
        type: 'set'
      }
    ]
  }
  const diffs: Different[] = []
  for (const key of new Set([...sourceKeys, ...targetKeys])) {
    if (isPrivateKey(key)) continue
    else if (!(key in source)) {
      diffs.push({
        oldValue: target[key],
        path: [...path, key],
        type: 'delete'
      })
    } else if (
      isPrimitiveDiff(target[key], source[key]) &&
      target[key] !== source[key]
    ) {
      diffs.push({
        newValue: source[key],
        oldValue: target[key],
        path: [...path, key],
        type: 'set'
      })
    } else {
      diffs.push(
        ...different(target[key], source[key], [...path, key], visitor)
      )
    }
  }
  return diffs
}

export function primitiveDiff(
  target?: any,
  source?: any,
  path: PropertyKey[] = []
): Different[] {
  if (target === source) return []
  return [
    {
      newValue: source,
      oldValue: target,
      path,
      type: 'set'
    }
  ]
}

function isPrimitiveDiff(target?: any, source?: any): boolean {
  const typeTarget = typeof target
  const typeSource = typeof source
  if (typeTarget !== typeSource) return true
  const isArrTarget = isArray(target)
  const isArrSource = isArray(source)
  if (isArrTarget !== isArrSource) return true
  return typeTarget !== 'object' || target == null || source == null
}
